import { authenticatedFetch } from './authenticatedFetch';

/**
 * MEDICATION API MODULE - Patient Medication Management System
 * 
 * This module provides comprehensive API functions for managing patient medications
 * in the medical monitoring system. It handles CRUD operations for medication
 * records, including dosage tracking, scheduling, and medical history.
 * 
 * Key Features:
 * - Complete medication lifecycle management (add, edit, delete, view)
 * - Patient-specific medication filtering and organization
 * - Dosage and frequency tracking for medical compliance
 * - Medication history and timeline management
 * - Integration with patient medical records
 * 
 * Medical Data Management:
 * - Medication names and pharmaceutical information
 * - Dosage tracking with units and measurements
 * - Frequency scheduling (daily, weekly, as needed)
 * - Start and end dates for medication courses
 * - Medical notes and special instructions
 * - Patient-specific medication profiles
 */

/**
 * MEDICATION INTERFACE - Patient Medication Data Structure
 * 
 * Represents a complete medication record for a patient, including
 * all necessary information for medical management and compliance tracking.
 */
export interface Medication {
  med_id: number;              // Unique identifier for the medication record
  pat_id: number;              // Patient ID this medication is assigned to
  name: string;                 // Medication name (generic or brand name)
  starting_date: string;       // Date when medication was started (ISO format)
  ending_date?: string;        // Optional end date for medication course
  dosage?: string;             // Dosage amount and units (e.g., "10mg", "2 tablets")
  frequency?: string;          // How often to take medication (e.g., "twice daily", "as needed")
  notes?: string;             // Additional medical notes or special instructions
}

export interface MedicationFormData {
  name: string;
  dosage?: string;
  frequency?: string;
  starting_date: string;
  ending_date?: string;
  notes?: string;
}

export const defaultMedication: MedicationFormData = {
  name: '',
  dosage: '',
  frequency: '',
  starting_date: new Date().toISOString().split('T')[0],
  ending_date: '',
  notes: ''
};

// Load medications for a specific patient
export const loadMedications = async (patId: string): Promise<Medication[]> => {
  try {
    const response = await authenticatedFetch(`/api/medications/${patId}`);
    if (!response.ok) {
      throw new Error('Failed to load medications');
    }
    return await response.json();
  } catch (error) {
    console.error('Error loading medications:', error);
    return [];
  }
};

// Save a new medication
export const saveMedication = async (medication: MedicationFormData, patId: string): Promise<boolean> => {
  try {
    const response = await authenticatedFetch(`/api/medications/${patId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(medication),
    });
    return response.ok;
  } catch (error) {
    console.error('Error saving medication:', error);
    return false;
  }
};

// Update an existing medication
export const updateMedication = async (medicationId: string, medication: MedicationFormData, patId: string): Promise<boolean> => {
  try {
    const response = await authenticatedFetch(`/api/medications/${patId}/${medicationId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(medication),
    });
    return response.ok;
  } catch (error) {
    console.error('Error updating medication:', error);
    return false;
  }
};

// Delete a medication
export const deleteMedication = async (medicationId: string, patId: string): Promise<boolean> => {
  try {
    const response = await authenticatedFetch(`/api/medications/${patId}/${medicationId}`, {
      method: 'DELETE',
    });
    return response.ok;
  } catch (error) {
    console.error('Error deleting medication:', error);
    return false;
  }
};
